import { useState } from "react";
import { useTranslation } from "react-i18next";
import { EditorContent } from "@tiptap/react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@babackapp/ui/components/select";
import { FloatingPanelSeparator } from "@babackapp/ui/components/floating-panel";
import PanelContainer, {
  useBlockPanelPropsContext,
} from "../../lib/panel/panel-container";
import PanelContent from "../../lib/panel/panel-content";
import PanelTranslationState from "../../lib/panel/panel-translation-state";
import { EmailPanelToolbar } from "../paragraph/email-panel-toolbar";
import { INSTRUCTION_KEYS, type InstructionKey } from "./instructions";
import { useInstructionEditor } from "./email-panel-editor";
import type { ReturnShippingInstructionsBlockProps } from "./types";

/** Rich-text editor for the currently-selected instruction message. */
function InstructionEditor({ instructionKey }: { instructionKey: InstructionKey }) {
  const editor = useInstructionEditor(instructionKey);
  return (
    <div className="space-y-2">
      <EmailPanelToolbar editor={editor} />
      <EditorContent editor={editor} />
    </div>
  );
}

function ReturnShippingInstructionsPanelContent() {
  const { t } = useTranslation("editor-blocks-library");
  const { locale } = useBlockPanelPropsContext();
  const [selectedKey, setSelectedKey] =
    useState<InstructionKey>("customerCarrier");

  return (
    <>
      <PanelContent
        title={t(
          "editorBlocksLibrary.blocks.return-shipping-instructions.panel.message.title",
          "Message to customize",
        )}
      >
        <Select
          value={selectedKey}
          onValueChange={(value) => setSelectedKey(value as InstructionKey)}
        >
          <SelectTrigger className="w-full h-8 text-sm bg-background">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {INSTRUCTION_KEYS.map((def) => (
              <SelectItem key={def.id} value={def.id}>
                {t(def.labelKey, def.labelFallback)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </PanelContent>
      <PanelContent
        title={t(
          "editorBlocksLibrary.blocks.return-shipping-instructions.panel.content.title",
          "Custom text",
        )}
      >
        {/* Remount on message OR locale change so the editor re-initializes both
            its content and its (locale-dependent) placeholder. */}
        <InstructionEditor
          key={`${locale}:${selectedKey}`}
          instructionKey={selectedKey}
        />
      </PanelContent>
      <FloatingPanelSeparator />
      <PanelTranslationState mode="translatable" portal />
    </>
  );
}

export function ReturnShippingInstructionsPanelRenderer(
  blockProps: ReturnShippingInstructionsBlockProps,
) {
  return (
    <PanelContainer blockProps={blockProps}>
      <ReturnShippingInstructionsPanelContent />
    </PanelContainer>
  );
}
